import { useEffect, useRef, useState } from "react";
import { describeError } from "../../api/client";
import { Field } from "../../components/Field";
import { FeedbackPanel } from "../../components/FeedbackPanel";
import { FloatingPanel } from "../../components/FloatingPanel";
import { useQuery } from "../../state/useQuery";
import { getEmpresaOptions, getEmpresaOptionsPage } from "./api";

/**
 * odd/tasks/campaign-audience-empresa-multiselect.md (T3): multi-select
 * rebuild of the Empresa combobox -- was a single-value ARIA-combobox
 * (`value: string`, `onChange: (empresa: string) => void`), now
 * `value: string[]` / `onChange: (empresas: string[]) => void`. The search
 * box stays exactly as it was (server-side `q`/`showId`, debounced 250ms,
 * stale-page tagging, keyboard navigation, Show-change validation) -- see
 * odd/tasks/empresa-options-search-by-show.md for that history, preserved
 * below almost verbatim. What changed is what an option DOES: clicking or
 * pressing Enter on it now TOGGLES membership in `value` without closing the
 * listbox (so several companies can be picked in one sitting), selected
 * companies render as removable chips above the input, and the typed text is
 * now PURELY a search query -- it no longer doubles as "the committed
 * value" the way it did for the single-select (there is no single value to
 * mirror into the input anymore).
 *
 * `allowEmpty` is gone: a multi-select's empty selection IS "todas las
 * empresas" by construction, there is no separate "required, must pick one"
 * mode to support (neither consumer -- `AudienceFilterEditor`,
 * `CampaignCreatePanel` -- ever passed `allowEmpty={false}`).
 */
const SEARCH_DEBOUNCE_MS = 250;
const DEFAULT_MAX_SELECTED = 100;
// panel-feedback-round2 T2: every company must be reachable. Pages of this size (the backend's
// own cap, `AdminContactSqlBuilder.NormalizeSize`) are requested with an explicit `offset`; the
// response's `hasMore` says whether another page exists. The listbox loads the next one when it
// is scrolled within this many pixels of the bottom.
const OPTIONS_PAGE_SIZE = 50;
const LOAD_MORE_SCROLL_THRESHOLD_PX = 48;

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timeoutId = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timeoutId);
  }, [value, delayMs]);
  return debounced;
}

interface EmpresaOptionsPage {
  showId: string | null;
  q: string | null;
  items: string[];
  /** Whether the server has more companies after this page (absent => no). */
  hasMore?: boolean;
}

/** Pages loaded AFTER the base query's first page, tagged with the `{showId, q}` they belong to. */
interface ExtraPages {
  tag: string;
  items: string[];
  /** Raw number of rows received (before de-duplication) -- the next request's offset base. */
  rawCount: number;
  hasMore: boolean;
  loading: boolean;
  error: boolean;
}

/**
 * Selected companies as removable pills (prototype step 2: 30px pills, `#eff6ff` / `#1d4ed8`,
 * "name ×"), meant to render BELOW the audience filter grid at full width. `EmpresaSelector`
 * takes `hideChips` when a parent renders this instead of the inline chips.
 */
export function EmpresaChips({
  value,
  onChange,
  disabled,
}: {
  value: string[];
  onChange: (empresas: string[]) => void;
  disabled?: boolean;
}) {
  if (value.length === 0) return null;
  return (
    <div className="v2-empresa-chips" aria-label="Empresas seleccionadas">
      {value.map((name) => (
        <button
          type="button"
          className="v2-empresa-chips__pill"
          key={name}
          aria-label={`Quitar ${name}`}
          disabled={disabled}
          onClick={() => onChange(value.filter((selected) => selected !== name))}
        >
          {name}
          <span aria-hidden="true"> ×</span>
        </button>
      ))}
      <button type="button" className="v2-empresa-chips__clear" disabled={disabled} onClick={() => onChange([])}>
        Limpiar
      </button>
    </div>
  );
}

/** Order-insensitive membership comparison -- used to detect whether the
 * committed selection changed WHILE a Show-change validation batch was in
 * flight (see `startShowValidation` below). */
function sameMembers(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((name, index) => name === sortedB[index]);
}

export function EmpresaSelector({
  id,
  value,
  onChange,
  disabled,
  showId,
  onValidationChange,
  maxSelected = DEFAULT_MAX_SELECTED,
  hideChips = false,
}: {
  id: string;
  value: string[];
  onChange: (empresas: string[]) => void;
  disabled?: boolean;
  // User follow-up 2026-09-28 ("que cuando en el campo de Show, seleccione
  // uno en específico en el campo de empresa sólo me aparezcan las empresas
  // asociadas a ese Show"): optional -- omitted entirely by
  // `AudienceFilterEditor` (filters an EXISTING campaign by an already-fixed
  // Show), passed by `CampaignCreatePanel`.
  showId?: string;
  // odd/tasks/campaign-audience-empresa-multiselect.md (T3, item 2): the
  // Show-change validation below now re-checks EVERY selected company
  // against the new Show (never just one) and drops the ones that no longer
  // exist for it -- same "never silently ship an unconfirmed combination"
  // contract as the single-select version.
  onValidationChange?: (state: "valid" | "pending" | "error") => void;
  // odd/tasks/campaign-audience-empresa-multiselect.md (T3, item 5): max 100
  // companies, enforced client-side with a clear message -- mirrors the
  // backend's own cap (`AdminAudienceFilterSelectionValidation`).
  maxSelected?: number;
  // panel-feedback-round2 T2: the parent renders `EmpresaChips` below its filter
  // grid (prototype layout) instead of the chips above this input.
  hideChips?: boolean;
}) {
  const listboxId = `${id}-listbox`;
  const containerRef = useRef<HTMLDivElement>(null);
  // The listbox is portaled (FloatingPanel) so a modal body's `overflow-y:
  // auto` can no longer clip it; the input is its anchor and the panel needs
  // its own ref for the "is this still inside the control" checks below.
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [inputText, setInputText] = useState("");
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [maxReachedWarning, setMaxReachedWarning] = useState(false);

  const searchTerm = inputText.trim();
  const debouncedSearch = useDebouncedValue(searchTerm, SEARCH_DEBOUNCE_MS);

  const normalizedShowId = showId ?? null;
  const normalizedDebouncedQ = debouncedSearch || null;

  const { data, error, loading, refreshing, stale, refetch } = useQuery<EmpresaOptionsPage>({
    key: ["empresa-options", normalizedShowId, normalizedDebouncedQ],
    queryFn: async () => {
      const result = await getEmpresaOptionsPage({
        showId: showId || undefined,
        q: debouncedSearch || undefined,
        offset: 0,
        size: OPTIONS_PAGE_SIZE,
      });
      return { showId: normalizedShowId, q: normalizedDebouncedQ, items: result.items, hasMore: result.hasMore };
    },
  });

  // Extra pages (offset > first page) are local state, tagged with the {showId, q} they were
  // loaded for so a Show/search change discards them (the base query restarts at offset 0).
  const pageTag = `${normalizedShowId ?? ""}|${normalizedDebouncedQ ?? ""}`;
  const [extra, setExtra] = useState<ExtraPages | null>(null);
  const pageTagRef = useRef(pageTag);
  pageTagRef.current = pageTag;
  const extraForTag = extra && extra.tag === pageTag ? extra : null;

  // Same currency/staleness contract as the single-select version (see its
  // historical comments in git blame) -- a loaded page is usable only when
  // it was fetched for EXACTLY the {showId, debouncedSearch} this render
  // wants, and keyboard highlighting additionally requires the debounce to
  // have caught up.
  //
  // Codex P2 (reported on PR #376, verified present on PR #378/this branch):
  // the page-tag comparison alone isn't enough to prove a page is CURRENT --
  // a remount that picks up an already cached `{showId, q}` page still fires
  // useQuery's own background refresh on mount, and when that refresh fails
  // the hook keeps serving the (now unconfirmed) cached `data` with
  // `stale: true` and `error` set, while the page tags still match exactly
  // (same key, nothing has changed). Without also checking `stale`, those
  // outdated options stayed selectable indefinitely with no retry control
  // (e.g. a company removed from the Show server-side could still be
  // committed into an audience that no longer matches it). `stale` is only
  // ever `true` together with `error` (useQuery.ts), and clears back to
  // `false` on the next successful fetch, so gating on it here also
  // automatically restores normal behavior once a retry succeeds.
  const isDebouncePending = searchTerm !== debouncedSearch;
  const optionsAreCurrent = data?.showId === normalizedShowId && data?.q === normalizedDebouncedQ && !stale;
  const baseItems = optionsAreCurrent ? (data?.items ?? []) : [];
  const visibleOptions = extraForTag
    ? [...baseItems, ...extraForTag.items.filter((name) => !baseItems.includes(name))]
    : baseItems;
  const hasMore = optionsAreCurrent && (extraForTag ? extraForTag.hasMore : (data?.hasMore ?? false));
  const loadingMore = extraForTag?.loading ?? false;
  const loadMoreFailed = extraForTag?.error ?? false;
  const hasActiveSearch = debouncedSearch !== "";
  const canNavigateOrCommit = optionsAreCurrent && !isDebouncePending;
  const navigableOptions = canNavigateOrCommit ? visibleOptions : [];

  const notCurrentReason: "loading" | "error" | null = optionsAreCurrent
    ? null
    : !loading && !refreshing && error !== undefined
      ? "error"
      : "loading";

  useEffect(() => {
    if (!canNavigateOrCommit) setHighlightedIndex(-1);
  }, [canNavigateOrCommit]);

  /** Requests the next page after everything loaded so far; ignores a response for a stale {showId, q}. */
  function loadMore() {
    if (!canNavigateOrCommit || !hasMore || loadingMore) return;
    const tag = pageTag;
    const offset = (data?.items.length ?? 0) + (extraForTag?.rawCount ?? 0);
    setExtra((prev) => ({
      tag,
      items: prev && prev.tag === tag ? prev.items : [],
      rawCount: prev && prev.tag === tag ? prev.rawCount : 0,
      hasMore: true,
      loading: true,
      error: false,
    }));
    getEmpresaOptionsPage({
      showId: showId || undefined,
      q: debouncedSearch || undefined,
      offset,
      size: OPTIONS_PAGE_SIZE,
    }).then(
      (result) => {
        if (pageTagRef.current !== tag) return;
        setExtra((prev) => {
          const base = prev && prev.tag === tag ? prev : null;
          return {
            tag,
            items: [...(base?.items ?? []), ...result.items],
            rawCount: (base?.rawCount ?? 0) + result.items.length,
            hasMore: result.hasMore,
            loading: false,
            error: false,
          };
        });
      },
      () => {
        if (pageTagRef.current !== tag) return;
        setExtra((prev) => (prev && prev.tag === tag ? { ...prev, loading: false, error: true } : prev));
      },
    );
  }

  function handleListboxScroll(event: React.UIEvent<HTMLUListElement>) {
    const el = event.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight <= LOAD_MORE_SCROLL_THRESHOLD_PX) loadMore();
  }

  const valueRef = useRef(value);
  valueRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onValidationChangeRef = useRef(onValidationChange);
  onValidationChangeRef.current = onValidationChange;
  const previousShowIdRef = useRef(showId);

  const [showValidationPending, setShowValidationPending] = useState(false);
  const [showValidationError, setShowValidationError] = useState(false);
  const cancelShowValidationRef = useRef<() => void>(() => {});

  /**
   * odd/tasks/campaign-audience-empresa-multiselect.md (T3, item 2): runs
   * the dedicated Show-change existence check for EVERY company in
   * `committedValues` (one `exact` lookup each, in parallel) -- drops
   * whichever ones no longer exist for `targetShowId`, keeps the rest.
   *
   * Review finding fixed here (T3, item 3): the single-select version's
   * `.then`/`.catch` returned EARLY, before clearing `showValidationPending`,
   * whenever the committed value had changed mid-flight -- so
   * `validationStatus` stayed stuck on `'pending'` forever once that
   * happened (e.g. the operator changed the selection while a Show-change
   * check was still in flight), and a parent wired to `onValidationChange`
   * (`CampaignCreatePanel`) kept "Crear campaña" disabled with no way out.
   * Fixed by always resolving `showValidationPending` to `false` as soon as
   * THIS check settles (right after the `cancelled` guard), regardless of
   * whether the result is then discarded as stale -- staleness only ever
   * gates whether the RESULT is applied (`onChange`/`showValidationError`),
   * never the pending flag itself.
   *
   * F5 (Codex P2, slice 3 review): the fix above still reported the stale
   * check's outcome as `validationStatus = 'valid'` whenever it was
   * superseded by a selection change -- correct when the selection was
   * CLEARED (nothing left to validate), but wrong when it merely changed
   * (chip removed / added): the CURRENT selection was never actually
   * checked against the current Show, yet the status claimed it was fine.
   * `restartForCurrentSelectionIfSuperseded` below is the single place both
   * the `.then` and `.catch` branches call once a check settles and finds
   * itself stale: an empty current selection is valid without a request (no
   * restriction to check), otherwise it restarts `startShowValidation` for
   * the CURRENT selection against the SAME target Show (reaching this point
   * already proves no newer Show change cancelled this check, so
   * `targetShowId` is still the current Show).
   */
  function restartForCurrentSelectionIfSuperseded(targetShowId: string | undefined): void {
    const currentValues = valueRef.current;
    if (currentValues.length === 0) {
      setShowValidationError(false);
      return;
    }
    startShowValidation(targetShowId, currentValues);
  }

  function startShowValidation(targetShowId: string | undefined, committedValues: string[]) {
    cancelShowValidationRef.current();
    let cancelled = false;
    cancelShowValidationRef.current = () => {
      cancelled = true;
    };
    setShowValidationError(false);
    setShowValidationPending(true);
    Promise.all(
      committedValues.map((name) =>
        getEmpresaOptions({ showId: targetShowId || undefined, exact: name }).then((matches) => ({
          name,
          stillValid: matches.some((match) => match.toLowerCase() === name.toLowerCase()),
        })),
      ),
    )
      .then((results) => {
        if (cancelled) return;
        setShowValidationPending(false);
        // Only act on this batch's result if the committed selection is
        // STILL exactly what we started checking -- a newer selection
        // change (or another Show change) already superseded it. F5: a
        // superseded check restarts for the current selection instead of
        // silently reporting 'valid' for a selection it never checked.
        if (!sameMembers(valueRef.current, committedValues)) {
          restartForCurrentSelectionIfSuperseded(targetShowId);
          return;
        }
        const stillValid = results.filter((result) => result.stillValid).map((result) => result.name);
        if (stillValid.length !== committedValues.length) {
          onChangeRef.current(stillValid);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setShowValidationPending(false);
        if (!sameMembers(valueRef.current, committedValues)) {
          restartForCurrentSelectionIfSuperseded(targetShowId);
          return;
        }
        setShowValidationError(true);
      });
  }

  // Only a REAL `showId` prop change (never the initial mount) runs this
  // check -- same narrow trigger as the single-select version.
  useEffect(() => {
    const previousShowId = previousShowIdRef.current;
    previousShowIdRef.current = showId;
    if (previousShowId === showId) return undefined;

    const committedValues = valueRef.current;
    if (committedValues.length === 0) {
      setShowValidationPending(false);
      setShowValidationError(false);
      return undefined;
    }

    startShowValidation(showId, committedValues);
    return () => {
      cancelShowValidationRef.current();
    };
  }, [showId]);

  const validationStatus: "valid" | "pending" | "error" = showValidationPending
    ? "pending"
    : showValidationError
      ? "error"
      : "valid";
  useEffect(() => {
    onValidationChangeRef.current?.(validationStatus);
  }, [validationStatus]);
  useEffect(() => {
    return () => onValidationChangeRef.current?.("valid");
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    function handlePointerDown(event: MouseEvent) {
      const target = event.target as Node;
      if (containerRef.current && !containerRef.current.contains(target) && !panelRef.current?.contains(target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  // Codex P2 (PR #376 second pass, finding 4): `disabled` can flip to `true`
  // WHILE the popup is open (e.g. `AudienceFilterEditor` re-rendering with
  // stale state) -- nothing closed it, so options stayed visible AND
  // clickable through a control the caller just disabled.
  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  useEffect(() => {
    if (!open || highlightedIndex < 0) return;
    const optionEl = document.getElementById(`${id}-option-${highlightedIndex}`);
    optionEl?.scrollIntoView?.({ block: "nearest" });
  }, [open, highlightedIndex, id]);

  /**
   * odd/tasks/campaign-audience-empresa-multiselect.md (T3, item 1): toggles
   * membership -- never closes the listbox, so several companies can be
   * picked in one sitting. Enforces the max-100 guard (item 5) only when
   * ADDING a new company (removing is always allowed).
   */
  // Codex P2 (PR #376 second pass, finding 4): every mutation guarded here --
  // a stray commit/toggle/chip-removal must never go through once the caller
  // has disabled the control, even for an element that was still rendered
  // the instant `disabled` flipped true (the closing effect above is
  // best-effort cleanup, not the only line of defense).
  function toggle(name: string) {
    if (disabled) return;
    setMaxReachedWarning(false);
    if (value.includes(name)) {
      onChange(value.filter((selected) => selected !== name));
      return;
    }
    if (value.length >= maxSelected) {
      setMaxReachedWarning(true);
      return;
    }
    onChange([...value, name]);
  }

  function removeOne(name: string) {
    if (disabled) return;
    setMaxReachedWarning(false);
    onChange(value.filter((selected) => selected !== name));
  }

  function clearAll() {
    if (disabled) return;
    setMaxReachedWarning(false);
    onChange([]);
  }

  // Codex P2 (PR #376 second pass, finding 3): a real pointer click on the
  // listbox's own "Reintentar" fires mousedown BEFORE click -- mousedown
  // blurred the input, which closed the popup and unmounted the button
  // before its click ever arrived. Tab out of the input had the same
  // problem: moving focus away closed the popup before focus could land on
  // anything inside it. Mirrors the option rows' own mousedown
  // preventDefault (below) for pointer interactions, and additionally keeps
  // the popup open for a FocusOut whose `relatedTarget` is still inside this
  // control, so Tab can reach the button instead of racing its own removal.
  function handleBlur(event: React.FocusEvent<HTMLInputElement>) {
    const next = event.relatedTarget as Node | null;
    if (next && (containerRef.current?.contains(next) || panelRef.current?.contains(next))) {
      return;
    }
    setOpen(false);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        setHighlightedIndex(navigableOptions.length > 0 ? 0 : -1);
        return;
      }
      if (navigableOptions.length > 0 && highlightedIndex >= navigableOptions.length - 1) loadMore();
      setHighlightedIndex((current) =>
        navigableOptions.length === 0 ? -1 : Math.min(current + 1, navigableOptions.length - 1),
      );
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedIndex((current) => (navigableOptions.length === 0 ? -1 : Math.max(current - 1, 0)));
      return;
    }
    if (event.key === "Enter") {
      // Never falls through to native form submission while open, even with
      // nothing highlighted -- only toggles when a valid option IS
      // highlighted. Closed keeps native behavior.
      if (open) {
        event.preventDefault();
        if (highlightedIndex >= 0) {
          const picked = navigableOptions[highlightedIndex];
          if (picked !== undefined) toggle(picked);
        }
      }
      return;
    }
    if (event.key === "Escape") {
      if (open) {
        event.preventDefault();
        event.stopPropagation();
        setOpen(false);
      }
    }
  }

  return (
    <div className="v2-empresa-selector" ref={containerRef}>
      {loading && <p role="status">Cargando empresas…</p>}

      {!data && error !== undefined && (
        <FeedbackPanel variant="error" message={describeError(error, "No se pudieron cargar las empresas.")} onRetry={refetch} />
      )}

      {data && (
        <>
          {
          // Codex P2 (PR #376 second pass, finding 1): a committed company awaiting
          // Show-change confirmation (`showValidationPending`/`showValidationError`)
          // keeps the Field (chips + validation error/retry) rendered even when the
          // new Show's options page is genuinely empty -- otherwise this branch
          // replaced the whole field, hiding the retry button while a parent gated
          // on `onValidationChange` (CampaignCreatePanel) stayed blocked with no
          // visible way out.
          !hasActiveSearch &&
          optionsAreCurrent &&
          (data?.items?.length ?? 0) === 0 &&
          !showValidationPending &&
          !showValidationError ? (
            <>
              <span className="v2-empresa-selector__label">Empresa</span>
              <FeedbackPanel variant="empty" message="No hay empresas disponibles." />
            </>
          ) : (
            <Field label="Empresa" htmlFor={id}>
              {!hideChips && value.length > 0 && (
                <div className="v2-empresa-selector__chips" aria-label="Empresas seleccionadas">
                  {value.map((name) => (
                    <span className="v2-empresa-selector__chip" key={name}>
                      <span className="v2-empresa-selector__chip-label">{name}</span>
                      <button
                        type="button"
                        className="v2-empresa-selector__chip-remove"
                        aria-label={`Quitar ${name}`}
                        disabled={disabled}
                        onClick={() => removeOne(name)}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  <button
                    type="button"
                    className="v2-empresa-selector__clear"
                    disabled={disabled}
                    onClick={clearAll}
                  >
                    Limpiar
                  </button>
                </div>
              )}
              <input
                id={id}
                ref={inputRef}
                type="text"
                role="combobox"
                autoComplete="off"
                aria-expanded={open}
                aria-controls={listboxId}
                aria-autocomplete="list"
                aria-activedescendant={highlightedIndex >= 0 ? `${id}-option-${highlightedIndex}` : undefined}
                value={inputText}
                disabled={disabled}
                placeholder={value.length === 0 ? "Todas las empresas" : "Agregar otra empresa…"}
                onFocus={() => setOpen(true)}
                onChange={(event) => {
                  setInputText(event.target.value);
                  setOpen(true);
                  setHighlightedIndex(-1);
                }}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
              />
              <span className="v2-modal-v2__hint">Puedes elegir varias; déjalo vacío para incluir todas las empresas.</span>
              {maxReachedWarning && (
                <p role="alert">No puedes seleccionar más de {maxSelected} empresas.</p>
              )}
              {showValidationError && (
                <FeedbackPanel
                  variant="error"
                  message="No se pudo confirmar las empresas seleccionadas para el Show seleccionado."
                  onRetry={() => startShowValidation(showId, valueRef.current)}
                />
              )}
              <FloatingPanel anchorRef={inputRef} panelRef={panelRef} open={open}>
                <ul
                  id={listboxId}
                  role="listbox"
                  aria-multiselectable="true"
                  className="v3-combobox__listbox"
                  onScroll={handleListboxScroll}
                >
                  {notCurrentReason === "loading" ? (
                    <li className="v3-combobox__status" aria-disabled="true">
                      Cargando empresas…
                    </li>
                  ) : notCurrentReason === "error" ? (
                    <li className="v3-combobox__status" aria-disabled="true">
                      No se pudieron cargar las empresas.{" "}
                      <button
                        type="button"
                        // mousedown (not click/onClick) fires BEFORE the input's own
                        // blur -- preventDefault keeps focus on the input (and the
                        // listbox open) exactly like the option rows above, so a real
                        // pointer click reaches this button instead of it being
                        // unmounted by a blur-triggered close first.
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={refetch}
                      >
                        Reintentar
                      </button>
                    </li>
                  ) : visibleOptions.length === 0 ? (
                    <li className="v3-combobox__status" aria-disabled="true">
                      Ninguna empresa coincide.
                    </li>
                  ) : (
                    visibleOptions.map((name, index) => {
                      const selected = value.includes(name);
                      return (
                        <li
                          key={name}
                          id={`${id}-option-${index}`}
                          role="option"
                          aria-selected={selected}
                          className={
                            [
                              "v3-combobox__option v3-combobox__option--multi",
                              index === highlightedIndex ? "v3-combobox__option--active" : "",
                              selected ? "v3-combobox__option--selected" : "",
                            ]
                              .filter(Boolean)
                              .join(" ")
                          }
                          // mousedown (not click/onClick) fires BEFORE the input's
                          // own blur -- preventDefault keeps focus on the input
                          // (and the listbox open) so the toggle never gets cut
                          // short by a blur-triggered close.
                          onMouseDown={(event) => {
                            event.preventDefault();
                            toggle(name);
                          }}
                        >
                          {name}
                        </li>
                      );
                    })
                  )}
                  {notCurrentReason === null && loadingMore && (
                    <li className="v3-combobox__status" aria-disabled="true">
                      Cargando más empresas…
                    </li>
                  )}
                  {notCurrentReason === null && loadMoreFailed && (
                    <li className="v3-combobox__status" aria-disabled="true">
                      No se pudieron cargar más empresas.{" "}
                      <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={loadMore}>
                        Reintentar
                      </button>
                    </li>
                  )}
                  {notCurrentReason === null && hasMore && !loadingMore && !loadMoreFailed && (
                    <li className="v3-combobox__status">
                      <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={loadMore}>
                        Cargar más empresas
                      </button>
                    </li>
                  )}
                </ul>
              </FloatingPanel>
            </Field>
          )}
        </>
      )}
    </div>
  );
}
